import React, { useEffect, useState } from "react";
import "./bdmcontrols.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const AdminProductTable = () => {
  const [products, setProducts] = useState([]);
  const [config, setConfig] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [searchAll, setSearchAll] = useState(""); 
  const [searchLow, setSearchLow] = useState(""); 
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // Collapsible state
  const [openAllProducts, setOpenAllProducts] = useState(true);
  const [openLowStock, setOpenLowStock] = useState(true);

  useEffect(() => {
    fetch("/config.json")
      .then((res) => res.json())
      .then((data) => setConfig(data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (config) fetchProducts(config);
  }, [config]);

  const fetchProducts = (configData) => {
    fetch(`${configData.BASE_URL}${configData.GET_TOTAL_PRODUCTS}`, {
      method: "POST",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.products)) {
          const mappedData = data.products.map((item, index) => ({
            id: index + 1,
            sku: item.product_id,
            name: item.title,
            boxno: item.boxno,
            quantity: item.quantity || 0,
            price: item.price,
            method: item.status1 === 1 ? "Quote" : "Razor Pay",
            status: item.status2 === 1 ? "Open" : "Hide",
            media: [
              item.image_path,
              item.image1,
              item.image2,
              item.image3,
              item.image4,
              item.videofile,
            ].filter(Boolean),
          }));
          setProducts(mappedData);
        }
      })
      .catch(console.error);
  };

  const lowStockProducts = products.filter((p) => Number(p.quantity) < 10);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setCurrentIndex(0);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
    setCurrentIndex(0);
  };

  const prevMedia = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? selectedProduct.media.length - 1 : prev - 1
    );
  };

  const nextMedia = () => {
    setCurrentIndex((prev) =>
      prev === selectedProduct.media.length - 1 ? 0 : prev + 1
    );
  };

  // Filter products based on search
  const filteredProducts = products.filter((item) =>
    Object.values(item)
      .join(" ")
      .toLowerCase()
      .includes(searchAll.toLowerCase())
  );

  const filteredLowStock = lowStockProducts.filter((item) =>
    Object.values(item)
      .join(" ")
      .toLowerCase()
      .includes(searchLow.toLowerCase())
  );

  const downloadExcel = (data, filename) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, filename);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    else if (sortConfig.key === key && sortConfig.direction === "desc") direction = null;
    setSortConfig({ key, direction });
  };

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (!sortConfig.key || !sortConfig.direction) return 0;
    const valA = a[sortConfig.key];
    const valB = b[sortConfig.key];

    if (["sku", "boxno", "price", "quantity"].includes(sortConfig.key)) {
      return sortConfig.direction === "asc" ? valA - valB : valB - valA;
    }

    if (sortConfig.key === "name") {
      return sortConfig.direction === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }

    if (sortConfig.key === "method") {
      return sortConfig.direction === "asc"
        ? valA === "Quote" ? -1 : 1
        : valA === "Razor Pay" ? -1 : 1;
    }

    if (sortConfig.key === "status") {
      return sortConfig.direction === "asc"
        ? valA === "Open" ? -1 : 1
        : valA === "Hide" ? -1 : 1;
    }

    return 0;
  });

  return (
    <div className="admin-products-table-page">

      {/* --- All Products --- */}
      <div className="table-section">
        <div
          className="table-header"
          onClick={() => setOpenAllProducts(!openAllProducts)}
          style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <h2>Admin Product Inventory</h2>
          <span style={{ fontSize: "18px" }}>{openAllProducts ? "▼" : "►"}</span>
        </div>

        {openAllProducts && (
          <div className="table-content">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <input
                type="text"
                placeholder="Search products..."
                value={searchAll}
                onChange={(e) => setSearchAll(e.target.value)}
                style={{
                  padding: "8px 12px",
                  width: "300px",
                  borderRadius: "20px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              />

              <button
                style={{
                  padding: "8px 15px",
                  backgroundColor: "#4CAF50",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  height: "35px",
                }}
                onClick={() => downloadExcel(filteredProducts, "All_Products.xlsx")}
              >
                Download Excel
              </button>
            </div>

            <table className="admin-products-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>SKU NO</th>
                  <th>Product Name</th>
                  <th>Box-No</th>
                  <th>Price</th>
                  <th>Quantity-left</th>
                  <th>Method</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length > 0 ? (
                  filteredProducts.map((item) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>{item.sku}</td>
                      <td>
                        <span
                          className="apt-modal-title"
                          onClick={() => handleProductClick(item)}
                        >
                          {item.name}
                        </span>
                      </td>
                      <td>{item.boxno}</td>
                      <td>{item.price}</td>
                      <td>{item.quantity}</td>
                      <td>{item.method}</td>
                      <td>
                        <span className={`status-badge status-${item.status.toLowerCase()}`}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center" }}>
                      No products found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* --- Low Stock Products --- */}
      <div className="table-section">
        <div
          className="table-header"
          onClick={() => setOpenLowStock(!openLowStock)}
          style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "40px" }}
        >
          <h2>Low Stock Products (Below 10)</h2>
          <span style={{ fontSize: "18px" }}>{openLowStock ? "▼" : "►"}</span>
        </div>

        {openLowStock && (
          <div className="table-content">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", marginTop: "20px" }}>
              <input
                type="text"
                placeholder="Search low stock products..."
                value={searchLow}
                onChange={(e) => setSearchLow(e.target.value)}
                style={{
                  padding: "8px 12px",
                  width: "300px",
                  borderRadius: "20px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              />

              <button
                style={{
                  padding: "8px 15px",
                  backgroundColor: "#f44336",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  height: "35px",
                }}
                onClick={() => downloadExcel(filteredLowStock, "Low_Stock_Products.xlsx")}
              >
                Download Excel
              </button>
            </div>

            <table className="admin-products-table low-stock-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>SKU NO</th>
                  <th>Product Name</th>
                  <th>Box-No</th>
                  <th>Price</th>
                  <th>Quantity-left</th>
                  <th>Method</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredLowStock.length > 0 ? (
                  filteredLowStock.map((item) => (
                    <tr key={`low-${item.id}`}>
                      <td>{item.id}</td>
                      <td>{item.sku}</td>
                      <td>
                        <span
                          className="apt-modal-title"
                          onClick={() => handleProductClick(item)}
                        >
                          {item.name}
                        </span>
                      </td>
                      <td>{item.boxno}</td>
                      <td>{item.price}</td>
                      <td style={{ color: "red", fontWeight: "bold" }}>{item.quantity}</td>
                      <td>{item.method}</td>
                      <td>
                        <span className={`status-badge status-${item.status.toLowerCase()}`}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center" }}>
                      No low-stock products
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* --- Modal --- */}
      {showModal && selectedProduct && (
        <div className="apt-modal-overlay" onClick={closeModal}>
          <div className="apt-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{selectedProduct.name}</h3>

            <div className="apt-modal-media">
              {selectedProduct.media[currentIndex].endsWith(".mp4") ? (
                <video
                  src={`/product_thumbnails/${selectedProduct.sku}/${selectedProduct.media[currentIndex]}`}
                  controls
                  className="apt-modal-video"
                />
              ) : (
                <img
                  src={`/product_thumbnails/${selectedProduct.sku}/${selectedProduct.media[currentIndex]}`}
                  alt={`Product Media ${currentIndex + 1}`}
                  className="apt-modal-image"
                />
              )}
            </div>

            {selectedProduct.media.length > 1 && (
              <div className="apt-modal-nav">
                <button onClick={prevMedia}>&lt; Prev</button>
                <span>
                  {currentIndex + 1} / {selectedProduct.media.length}
                </span>
                <button onClick={nextMedia}>Next &gt;</button>
              </div>
            )}

            <button className="apt-modal-close" onClick={closeModal}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProductTable;
